"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CameraIcon, ImageIcon, ImagePlusIcon, SendIcon, XIcon } from "lucide-react";
import { toast } from "sonner";
import { ZoomPhotoDialog } from "@/components/zoom-photo-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { AuthedImage } from "@/components/authed-image";
import { SmoothReveal } from "@/components/smooth-reveal";
import { uniqueHomeworkPhotos } from "@/components/diary-task-line";
import { api } from "@/lib/api";
import { describeFetchError } from "@/lib/homework-diag";
import { homeworkDueLabel } from "@/lib/homework-due";
import { MAX_HOMEWORK_PHOTO_BYTES, MAX_HOMEWORK_PHOTOS, mimeFromUpload } from "@/lib/homework-limits";
import { captureHomeworkCameraFrame, needsHomeworkNormalize, normalizeHomeworkPhoto } from "@/lib/homework-photo";
import type { HomeworkParseRow, HomeworkPhotoDto, HomeworkTaskDto } from "@/lib/types";
import { cn } from "@/lib/utils";

type DraftPhoto = {
  id: string;
  file: File;
  url: string;
  ready: Promise<File>;
};

function tasksToRows(tasks: HomeworkTaskDto[]): HomeworkParseRow[] {
  return [...tasks]
    .sort((a, b) => (a.slotOrder ?? 99) - (b.slotOrder ?? 99) || a.subject.localeCompare(b.subject, "uk"))
    .map((task) => ({
      subject: task.subject,
      task: task.task,
      dueDate: task.dueDate,
      dueLabel: task.dueLabel,
      slotOrder: task.slotOrder,
      dueManual: true,
      photoIds: task.photos.map((photo) => photo.id),
    }));
}

function rowsFingerprint(rows: HomeworkParseRow[]) {
  return JSON.stringify(
    rows.map((row) => ({
      subject: row.subject.trim(),
      task: row.task.trim(),
      dueDate: row.dueDate,
    })),
  );
}

export function HomeworkCapturePanel({
  open,
  onOpenChange,
  onSaved,
  existingTasks = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
  existingTasks?: HomeworkTaskDto[];
}) {
  const [hint, setHint] = useState("");
  const [photos, setPhotos] = useState<DraftPhoto[]>([]);
  const [rows, setRows] = useState<HomeworkParseRow[]>([]);
  const [savedPhotos, setSavedPhotos] = useState<HomeworkPhotoDto[]>([]);
  const [updating, setUpdating] = useState(false);
  const [sending, setSending] = useState(false);
  const [openedPhoto, setOpenedPhoto] = useState<{
    photos: Array<{ url: string; createdAt?: string; authorName?: string }>;
    index: number;
    alt: string;
  } | null>(null);
  const [photoSourceOpen, setPhotoSourceOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [initialFingerprint, setInitialFingerprint] = useState("");
  const primed = useRef(false);

  useEffect(() => {
    if (!open) {
      primed.current = false;
      return;
    }
    if (primed.current) return;
    primed.current = true;
    const nextRows = tasksToRows(existingTasks);
    setHint("");
    setPhotos((current) => {
      for (const photo of current) URL.revokeObjectURL(photo.url);
      return [];
    });
    setRows(nextRows);
    setSavedPhotos(uniqueHomeworkPhotos(existingTasks));
    setUpdating(existingTasks.length > 0);
    setInitialFingerprint(rowsFingerprint(nextRows));
  }, [open, existingTasks]);

  useEffect(() => {
    return () => {
      for (const photo of photos) URL.revokeObjectURL(photo.url);
    };
  }, [photos]);

  function appendFiles(list: FileList | File[] | null, source: "gallery" | "camera") {
    if (!list) return [] as DraftPhoto[];
    const files = Array.isArray(list) ? list : Array.from(list);
    const added: DraftPhoto[] = [];
    setPhotos((current) => {
      const next = [...current];
      for (const file of files) {
        if (next.length + savedPhotos.length >= MAX_HOMEWORK_PHOTOS) {
          toast.error(`Можна до ${MAX_HOMEWORK_PHOTOS} фото.`);
          break;
        }
        const mime = mimeFromUpload(file.type, file.name);
        if (!mime) {
          toast.error("Потрібне фото: jpeg, png, webp, gif або heic.");
          continue;
        }
        if (file.size > MAX_HOMEWORK_PHOTO_BYTES) {
          toast.error("Одне фото має бути до 8 МБ.");
          continue;
        }
        const id = `${file.name}-${file.size}-${file.lastModified}-${next.length}-${Math.random().toString(36).slice(2, 7)}`;
        const url = URL.createObjectURL(file);
        const shouldNormalize = source === "gallery" && needsHomeworkNormalize(file);
        const ready = shouldNormalize
          ? normalizeHomeworkPhoto(file).catch((error) => {
              toast.error("Не вдалося підготувати фото з галереї.");
              setPhotos((latest) => latest.filter((item) => item.id !== id));
              throw error;
            })
          : Promise.resolve(file);
        const draft = { id, file, url, ready };
        next.push(draft);
        added.push(draft);
      }
      return next;
    });
    return added;
  }

  function addDraftFiles(files: FileList | File[] | null, source: "gallery" | "camera" = "gallery") {
    appendFiles(files, source);
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  }

  function removePhoto(id: string) {
    setPhotos((current) => {
      const item = current.find((photo) => photo.id === id);
      if (item) URL.revokeObjectURL(item.url);
      return current.filter((photo) => photo.id !== id);
    });
  }

  async function sendDraft() {
    const readyRows = rows.filter((row) => row.task.trim());
    if (!hint.trim() && photos.length === 0 && readyRows.length === 0) {
      toast.error("Додайте фото або текст.");
      return;
    }
    setSending(true);
    try {
      let result: {
        delivered: number;
        wouldNotify: number;
        staffOnly?: boolean;
        createdCount?: number;
        updatedCount?: number;
        unchangedCount?: number;
        skipped?: string[];
      };
      if (photos.length > 0 || (readyRows.length === 0 && hint.trim())) {
        const ready = await Promise.all(photos.map((photo) => photo.ready));
        const form = new FormData();
        form.set("hint", hint.trim());
        for (const file of ready) form.append("photos", file);
        result = await api("/api/homework/tasks", { method: "POST", body: form });
      } else {
        result = await api("/api/homework/tasks", {
          method: "POST",
          body: JSON.stringify({
            hint: hint.trim(),
            rows: readyRows,
          }),
        });
      }
      setHint("");
      setRows([]);
      setSavedPhotos([]);
      setPhotos((current) => {
        for (const photo of current) URL.revokeObjectURL(photo.url);
        return [];
      });
      if (result.skipped && result.skipped.length > 0) {
        toast.message(`Не знайшла урок для: ${result.skipped.join(", ")}.`);
      }
      if (result.delivered > 0) {
        const changedOnly = (result.updatedCount ?? 0) > 0 && (result.createdCount ?? 0) === 0;
        toast.success(
          result.staffOnly
            ? `Збережено. Сповіщення staff: ${result.delivered}.`
            : changedOnly
              ? `Надіслано зміни ${result.delivered} батькам.`
              : `Надіслано ${result.delivered} батькам.`,
        );
      } else if ((result.unchangedCount ?? 0) > 0) {
        toast.success("У щоденнику вже так само. Повідомлення не надсилалось.");
      } else {
        toast.success("Збережено в щоденнику.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося надіслати.");
    } finally {
      setSending(false);
    }
  }

  async function refreshDue(index: number, subject: string) {
    try {
      const next = await api<{ dueDate: string | null; dueLabel: string; slotOrder: number | null }>(
        `/api/homework/tasks?next=1&subject=${encodeURIComponent(subject)}`,
      );
      setRows((current) =>
        current.map((row, i) =>
          i === index && !row.dueManual
            ? {
                ...row,
                dueDate: next.dueDate,
                dueLabel: next.dueLabel,
                slotOrder: next.slotOrder,
              }
            : row,
        ),
      );
    } catch {
      // keep
    }
  }

  const dirty = photos.length > 0 || hint.trim().length > 0 || rowsFingerprint(rows) !== initialFingerprint;
  const canSend = updating ? dirty : hint.trim().length > 0 || photos.length > 0;
  const previewPhotos = [
    ...savedPhotos.map((photo) => ({
      id: photo.id,
      url: photo.url,
      draft: false,
      createdAt: photo.createdAt,
      authorName: photo.authorName,
    })),
    ...photos.map((photo) => ({ id: photo.id, url: photo.url, draft: true, createdAt: "", authorName: "" })),
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-h-[min(92dvh,var(--app-height,92dvh))] w-[calc(100%-1.5rem)] max-w-lg gap-3 overflow-y-auto rounded-3xl p-4 sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle>{updating ? "Оновити ДЗ" : "Задати ДЗ"}</DialogTitle>
            <DialogDescription>
              {updating
                ? "Це те, що вже задали сьогодні. Поправте текст або надішліть нове фото дошки. Батькам підуть лише зміни."
                : "Зробіть фото дошки або допишіть текст і натисніть «Надіслати». Модель сама прочитає завдання, запише в щоденник і надішле батькам."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            className="min-h-20 rounded-2xl bg-accent/60 px-3"
            placeholder="Можна дописати завдання словами, якщо на фото не все видно…"
            value={hint}
            onChange={(event) => setHint(event.target.value)}
          />
          {previewPhotos.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {previewPhotos.map((photo, index) => (
                <div key={photo.id} className="relative overflow-hidden rounded-2xl bg-accent">
                  <button
                    type="button"
                    className="block w-full"
                    onClick={() =>
                      setOpenedPhoto({
                        photos: previewPhotos.map((item) => ({
                          url: item.url,
                          createdAt: item.createdAt,
                          authorName: item.authorName,
                        })),
                        index,
                        alt: `Фото ${index + 1}`,
                      })
                    }
                  >
                    {photo.draft ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={photo.url} alt="" className="aspect-square w-full object-cover" />
                    ) : (
                      <AuthedImage src={photo.url} alt="" className="aspect-square w-full object-cover" />
                    )}
                  </button>
                  {photo.draft ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        removePhoto(photo.id);
                      }}
                      className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white"
                      aria-label="Прибрати фото"
                    >
                      <XIcon className="size-3.5" />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          {sending ? (
            <p className="text-xs text-muted-foreground">
              {photos.length > 0 ? "Читаю дошку й надсилаю… Це може зайняти пів хвилини." : "Надсилаю…"}
            </p>
          ) : null}
          <SmoothReveal open={rows.length > 0}>
            <div className="space-y-3">
              {rows.map((row, index) => (
                <div key={`${row.subject}-${index}`} className="space-y-2 rounded-2xl bg-accent/50 p-3">
                  <Input
                    className="h-10 rounded-2xl bg-card px-3"
                    value={row.subject}
                    placeholder="Предмет"
                    onChange={(event) =>
                      setRows((current) =>
                        current.map((item, i) => (i === index ? { ...item, subject: event.target.value } : item)),
                      )
                    }
                    onBlur={() => void refreshDue(index, row.subject)}
                  />
                  <Input
                    type="date"
                    className="h-10 rounded-2xl bg-card px-3"
                    value={row.dueDate ?? ""}
                    onChange={(event) =>
                      setRows((current) =>
                        current.map((item, i) =>
                          i === index
                            ? {
                                ...item,
                                dueDate: event.target.value,
                                dueManual: true,
                                dueLabel: event.target.value ? homeworkDueLabel(event.target.value) : "дата невідома",
                              }
                            : item,
                        ),
                      )
                    }
                  />
                  <p className="text-xs text-muted-foreground">На урок: {row.dueLabel}</p>
                  <Textarea
                    className="min-h-16 rounded-2xl bg-card px-3"
                    value={row.task}
                    onChange={(event) =>
                      setRows((current) =>
                        current.map((item, i) => (i === index ? { ...item, task: event.target.value } : item)),
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </SmoothReveal>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              className="h-11 flex-1 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={sending}
              onClick={() => setPhotoSourceOpen(true)}
            >
              <ImagePlusIcon />
              Фото
            </Button>
            <Button
              type="button"
              className={cn(
                "h-11 flex-[1.4] rounded-2xl disabled:opacity-100",
                canSend
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground",
              )}
              disabled={sending || !canSend}
              onClick={() => void sendDraft()}
            >
              <SendIcon />
              {sending ? "Надсилаю…" : "Надіслати"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => addDraftFiles(event.target.files, "gallery")}
      />
      <Dialog open={photoSourceOpen} onOpenChange={setPhotoSourceOpen}>
        <DialogContent showCloseButton={false} className="z-[60] gap-3 rounded-3xl p-4 sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Додати фото</DialogTitle>
            <DialogDescription>Зробити нове або взяти з галереї.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              className="h-12 justify-start rounded-2xl"
              onClick={() => {
                setPhotoSourceOpen(false);
                setCameraOpen(true);
              }}
            >
              <CameraIcon />
              Зробити фото
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 justify-start rounded-2xl"
              onClick={() => {
                galleryInputRef.current?.click();
                setPhotoSourceOpen(false);
              }}
            >
              <ImageIcon />
              З галереї
            </Button>
            <Button type="button" variant="ghost" className="h-11 rounded-2xl" onClick={() => setPhotoSourceOpen(false)}>
              Скасувати
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {cameraOpen && typeof document !== "undefined"
        ? createPortal(
            <CameraCaptureDialog
              onClose={() => setCameraOpen(false)}
              onCapture={(file) => {
                addDraftFiles([file], "camera");
                setCameraOpen(false);
              }}
            />,
            document.body,
          )
        : null}
      {openedPhoto && typeof document !== "undefined"
        ? createPortal(
            <ZoomPhotoDialog
              photos={openedPhoto.photos}
              startIndex={openedPhoto.index}
              alt={openedPhoto.alt}
              onClose={() => setOpenedPhoto(null)}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function CameraCaptureDialog({
  onClose,
  onCapture,
}: {
  onClose: () => void;
  onCapture: (file: File) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapping, setSnapping] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function bumpPreviewResolution(stream: MediaStream) {
      const track = stream.getVideoTracks()[0];
      if (!track?.getCapabilities || !track.applyConstraints) return;
      const caps = track.getCapabilities() as MediaTrackCapabilities;
      const maxW = caps.width?.max;
      const maxH = caps.height?.max;
      if (!maxW || !maxH) return;
      const longIdeal = Math.min(1920, Math.max(maxW, maxH));
      const shortIdeal = Math.min(1440, Math.min(maxW, maxH));
      const landscape = maxW >= maxH;
      try {
        await track.applyConstraints({
          width: { ideal: landscape ? longIdeal : shortIdeal },
          height: { ideal: landscape ? shortIdeal : longIdeal },
        });
      } catch {
        // keep stream
      }
    }

    async function openCameraStream() {
      const attempts: MediaStreamConstraints[] = [
        {
          audio: false,
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
        },
        { audio: false, video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } } },
        { audio: false, video: { facingMode: { ideal: "environment" } } },
        { audio: false, video: true },
      ];
      let lastError: unknown;
      for (const constraints of attempts) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          await bumpPreviewResolution(stream);
          return stream;
        } catch (error) {
          lastError = error;
        }
      }
      throw lastError ?? new Error("camera unavailable");
    }

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Камера недоступна в цьому клієнті Telegram.");
        return;
      }
      try {
        const stream = await openCameraStream();
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.setAttribute("playsinline", "true");
          video.muted = true;
          await video.play().catch(() => undefined);
        }
        setReady(true);
      } catch {
        setError("Не вдалося відкрити камеру. Дозвольте доступ у Telegram або візьміть фото з галереї.");
      }
    }

    void start();
    return () => {
      cancelled = true;
      for (const track of streamRef.current?.getTracks() ?? []) track.stop();
      streamRef.current = null;
    };
  }, []);

  async function snap() {
    const video = videoRef.current;
    if (!video || !ready || snapping) return;
    setSnapping(true);
    try {
      const file = await captureHomeworkCameraFrame(video);
      for (const track of streamRef.current?.getTracks() ?? []) track.stop();
      streamRef.current = null;
      onCapture(file);
    } catch (error) {
      const message = describeFetchError(error);
      toast.error(message.includes("8 МБ") ? message : "Не вдалося зробити знімок.");
      setSnapping(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-black text-white">
      <div className="flex items-center justify-between gap-3 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2">
        <p className="text-sm font-medium">Камера</p>
        <button type="button" className="rounded-full bg-white/15 p-2" aria-label="Закрити" onClick={onClose}>
          <XIcon className="size-5" />
        </button>
      </div>
      <div className="relative min-h-0 flex-1 bg-black">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className={cn("absolute inset-0 h-full w-full bg-black object-contain", (!ready || error) && "opacity-0")}
        />
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
            <p className="max-w-sm text-sm leading-6 text-white/90">{error}</p>
          </div>
        ) : !ready ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-sm text-white/70">Відкриваю камеру…</p>
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-center gap-4 px-4 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        {error ? (
          <Button type="button" variant="secondary" className="h-12 rounded-2xl" onClick={onClose}>
            Закрити
          </Button>
        ) : (
          <button
            type="button"
            disabled={!ready || snapping}
            onClick={() => void snap()}
            className="rounded-full border-4 border-white/85 bg-white/95 disabled:opacity-40"
            aria-label="Зняти"
            style={{ width: "4.5rem", height: "4.5rem" }}
          />
        )}
      </div>
    </div>
  );
}
